import type { WorkspaceSubscription } from './types'

export type PlanTier = WorkspaceSubscription['plan_tier']
export type BillingCycle = NonNullable<WorkspaceSubscription['billing_cycle']>

export interface SubscriptionPlanDefinition {
  tier: PlanTier
  monthlyPriceCents: number
  annualPriceCents: number
  teamMemberLimit: number
  clientLimit: number | null
  recommended: boolean
  subtitle: string
  features: string[]
}

export const subscriptionPlans: SubscriptionPlanDefinition[] = [
  {
    tier: 'starter',
    monthlyPriceCents: 2_499,
    annualPriceCents: 23_999,
    teamMemberLimit: 2,
    clientLimit: 75,
    recommended: false,
    subtitle: 'For solo owners and small cleaning teams',
    features: [
      'Scheduling and two-sided confirmations',
      'In-app client chat with photo and video attachments',
      'Invoices and owner-only finance reports',
      'iPhone, iPad, and web access',
    ],
  },
  {
    tier: 'growth',
    monthlyPriceCents: 4_999,
    annualPriceCents: 47_999,
    teamMemberLimit: 10,
    clientLimit: 300,
    recommended: true,
    subtitle: 'For growing companies coordinating a larger team',
    features: [
      'Everything in Starter',
      'Team roles and cleaning assignments',
      'Scheduling, chat, invoices, and owner finance tools',
      'iPhone, iPad, and web access',
    ],
  },
  {
    tier: 'pro',
    monthlyPriceCents: 8_999,
    annualPriceCents: 85_999,
    teamMemberLimit: 30,
    clientLimit: null,
    recommended: false,
    subtitle: 'For established operations with high client volume',
    features: [
      'Everything in Growth',
      'Unlimited active clients',
      'Capacity for established cleaning operations',
      'iPhone, iPad, and web access',
    ],
  },
]

export function priceCents(plan: SubscriptionPlanDefinition, cycle: BillingCycle) {
  return cycle === 'annual' ? plan.annualPriceCents : plan.monthlyPriceCents
}

export function annualSavingsPercent(plan: SubscriptionPlanDefinition) {
  return Math.round((1 - plan.annualPriceCents / (plan.monthlyPriceCents * 12)) * 100)
}

export function subscriptionHasAccess(subscription: WorkspaceSubscription | null) {
  if (!subscription || !['trialing', 'active', 'grace_period'].includes(subscription.status)) return false
  const end = subscription.access_source === 'trial' || subscription.access_source === 'beta'
    ? subscription.trial_ends_at
    : subscription.current_period_ends_at
  return !end || new Date(end).getTime() > Date.now()
}

